import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const roleColor = (r) => (r === "super_admin" ? "bg-emerald-600" : "bg-blue-600");

const LEVELS = ["STATE", "DISTRICT", "ULB", "WARD", "AREA", "ZONE", "PLANT", "USER"];
const DESIGNATIONS = [
  "Engineer", "Chief Minister", "Nodal Officer", "Municipal Comissioner", "Director (SBM)",
  "Principal Secretary (State)", "Minister (Urban Development) for state", "PMU (National)",
  "PMU (State)", "Section Officer", "Executive Officer", "Councillor", "Secretary (MOHUA)",
  "Plant Incharge", "Ward Incharge", "Area Inspector", "Joint Secretary", "MIS operator", "Junior Engineer",
];
const EMPTY_FORM = { level: "", designation: "", name: "", contact_number: "", email: "", username: "", password: "", role: "" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);

  const isSuper = user?.role === "super_admin";
  const roleOptions = roles.map((r) => r.name);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); api.get("/roles").then(({ data }) => setRoles(data)).catch(() => {}); }, []);

  const openNew = () => { setEditUser(null); setForm({ ...EMPTY_FORM, role: roles[0]?.name || "" }); setShowForm(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ level: u.level || "", designation: u.designation || "", name: u.name || "", contact_number: u.contact_number || "", email: u.email || "", username: u.username || "", password: "", role: u.role }); setShowForm(true); };

  const save = async () => {
    const required = editUser
      ? ["level", "designation", "name", "contact_number", "email", "username", "role"]
      : ["level", "designation", "name", "contact_number", "email", "username", "password", "role"];
    const missing = required.filter((k) => !String(form[k] || "").trim());
    if (missing.length) { toast.error("All fields are mandatory"); return; }
    setSaving(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, {
          name: form.name, role: form.role, password: form.password || undefined,
          username: form.username, level: form.level, designation: form.designation, contact_number: form.contact_number,
        });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try { await api.delete(`/users/${deleteId}`); toast.success("User deleted"); setDeleteId(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sort = useSortableData(users);
  const sorted = sort.sorted;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Administration</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">User Management</h1>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead column="username" sort={sort}>Username</SortableHead>
              <SortableHead column="email" sort={sort}>Email</SortableHead>
              <SortableHead column="designation" sort={sort}>Designation</SortableHead>
              <SortableHead column="role" sort={sort}>Role</SortableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((u) => (
              <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.username || "—"}</TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell className="text-slate-600">{u.designation || "—"}</TableCell>
                <TableCell><Badge className={`${roleColor(u.role)} text-white capitalize`}>{u.role.replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                  {isSuper && u.id !== user.id && (
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="user-form-dialog" className="max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0"><DialogTitle className="font-heading">{editUser ? "Edit User" : "Add User"}</DialogTitle><DialogDescription className="text-sm text-slate-500">{editUser ? "Update the user's details and role." : "Create a new account and assign a role."}</DialogDescription></DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4 form-scroll">
            <div className="space-y-2">
              <Label>Select Level</Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="user-level"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Designation</Label>
              <Select value={form.designation} onValueChange={(v) => setForm({ ...form, designation: v })}>
                <SelectTrigger data-testid="user-designation"><SelectValue placeholder="Select designation" /></SelectTrigger>
                <SelectContent>{DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Contact Person Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div className="space-y-2"><Label>Contact Number</Label><Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} data-testid="user-contact-number" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} disabled={!!editUser} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username" /></div>
            <div className="space-y-2"><Label>{editUser ? "New Password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white shrink-0">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="user-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="user-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
